import { DataListRequest, DataListResponse } from '../models/dataList.model';
import { catchError, EMPTY, finalize, Observable, tap } from 'rxjs';
import { IDatasource, IGetRowsParams } from 'ag-grid-community';
import { signal } from '@angular/core';

export type GridDataSourceOptions<TFilter, T> = {
  fetchData: (request: DataListRequest<TFilter>) => Observable<DataListResponse<T>>;
  getFilter: () => TFilter;
  pageSize?: number;
};

export function createGridDataSource<TFilter, T>(options: GridDataSourceOptions<TFilter, T>) {
  const isLoading = signal(false);
  let activeRequests = 0;

  const loadData = (params: IGetRowsParams): void => {
    isLoading.set(true);
    activeRequests++;

    const pageSize = options.pageSize ?? 10;
    const page = Math.floor(params.startRow / pageSize);
    const sort = params.sortModel.map(s => `${s.colId},${s.sort}`).join(',');
    const filter = options.getFilter();

    const fetchParams: DataListRequest<TFilter> = { filter, page, sort, size: pageSize };

    options
      .fetchData(fetchParams)
      .pipe(
        tap(({ content, totalElements }) => {
          params.successCallback(content, totalElements);
        }),
        catchError(() => {
          params.failCallback();
          return EMPTY;
        }),
        finalize(() => {
          // if multiple requests are made, we only set loading to false when all requests are done
          activeRequests--;
          if (activeRequests === 0) {
            isLoading.set(false);
          }
        })
      )
      .subscribe();
  };

  const dataSource: IDatasource = {
    getRows: (params: IGetRowsParams) => loadData(params),
  };

  return { dataSource, isLoading };
}
